import React from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import {
  Panel,
} from 'react-bootstrap';

import ReactTable from 'react-table';
import moment from 'moment';

import NotificationDetails from './NotificationDetails';
import { setDailyTimeEntryStatus } from '../actions';

import {
  defaultTableFiltering,
  formatMonthDay,
} from '../../../utils';

import { queryAllWeeklyTimesheetsGQL } from '../graphql';
import NewTabLink from '../../../components/NewTabLink/index';
import { getUserTimesheetLink } from '../../timesheetNavigator/links';

const INACTIVE = 'inactive';
const YES = 'yes';
const NO = 'no';
const NOT_EXPECTED = 'not_expected';

const proptypes = exact({
  userTimesheets: PropTypes.array.isRequired,
  interval: PropTypes.array.isRequired,
  setDailyTimeEntryStatusAction: PropTypes.func.isRequired,
});

const TimesheetCollectionStatusLayout = (props) => {
  const returnHeader = (day) => {
    const { dayOfWeek, iso8601 } = day;

    return (
      <div>
        <span>{dayOfWeek}</span><br />
        <span>{formatMonthDay(iso8601)}</span>
      </div>
    );
  };

  const returnCellColorAndValue = (cellValue) => {
    /* NOTE: Setting color/value for inactive */
    const colorValueObject = {
      color: '#000000',
      value: '',
    };

    /* NOTE: Yes means it's expected, therefore false
     * No means it already has a timesheet, therefore true, one exsists
     * Inactive means not hired then / terminated, black and no value
     * Not expected is a special case of false, can toggle between false and not expect
     * False it's not there, not expected, don't expect to see a timesheet for that day */

    if (cellValue === NO) {
      return {
        color: '#7fed7d',
        value: 'true',
      };
    }

    if (cellValue === YES) {
      return {
        color: '#e0e0e0',
        value: 'false',
      };
    }

    if (cellValue === NOT_EXPECTED) {
      return {
        color: '#04C400',
        value: 'not expected',
      };
    }

    return colorValueObject;
  };

  const handleSetTimeEntryStatus = async (paramUserId, paramDateIso) => {
    /* NOTE: need user_id, date, expected value */
    const { setDailyTimeEntryStatusAction } = props;
    await setDailyTimeEntryStatusAction(paramUserId, paramDateIso);
  };

  // eslint-disable-next-line max-len
  const returnFormattedCell = (paramUser, paramUserCurrentDailyTimeEntry, paramDailyTimeEntries, pos) => {
    const { requiresTimeEntry } = paramUserCurrentDailyTimeEntry;
    const { color: cellColor, value: cellValue } = returnCellColorAndValue(requiresTimeEntry);

    const isInactive = (requiresTimeEntry === INACTIVE);
    if (isInactive) {
      return (
        <div style={{ backgroundColor: cellColor }}>
          <div>&nbsp;</div>
        </div>
      );
    }

    const hasTimesheet = (requiresTimeEntry === NO);
    if (hasTimesheet) {
      return (
        <div style={{ backgroundColor: cellColor }}>
          <span>{cellValue} </span>
        </div>
      );
    }

    const dateObj = paramDailyTimeEntries[pos].date;
    /* NOTE: Doesn't have timesheet, can toggle from false and not_expected */
    return (
      <div
        onClick={() => { return handleSetTimeEntryStatus(paramUser.id, dateObj.iso8601); }}
        style={{ backgroundColor: cellColor, cursor: 'pointer' }}
      >
        <span>{cellValue} </span>
      </div>
    );
  };

  const clickableUserCell = (user, dailyTimeEntries, userName) => {
    const latestDate = dailyTimeEntries[dailyTimeEntries.length - 1].date;
    const url = getUserTimesheetLink(user.id, moment(latestDate.iso8601));
    return (
      <div>
        <NewTabLink style={{ display: 'inline' }} to={url}>{userName}</NewTabLink>
      </div>
    );
  };


  const renderColumns = () => {
    const { interval } = props;
    const userColumns = [{
      Header: 'First Name',
      id: 'firstname',
      accessor: (allData) => {
        return allData.user.firstname;
      },
      Cell: (allData) => {
        const { user, dailyTimeEntries } = allData.original;
        return clickableUserCell(user, dailyTimeEntries, user.firstname);
      },
    },
    {
      Header: 'Last Name',
      id: 'lastname',
      accessor: (allData) => {
        return allData.user.lastname;
      },
      Cell: (allData) => {
        const { user, dailyTimeEntries } = allData.original;
        return clickableUserCell(user, dailyTimeEntries, user.lastname);
      },
    },
    {
      Header: 'District',
      id: 'districtName',
      accessor: (e) => { return e.user.default_district.name; },
    }];

    const dayColumns = interval.map((dayObject, index) => {
      return ({
        Header: returnHeader(dayObject),
        id: `${dayObject}${index}`,
        accessor: (allData) => {
          const { hasTimeEntries } = allData.dailyTimeEntries[index];
          const { requiresTimeEntry } = allData.userDailyTimeEntries[index];

          /* NOTE: returning not_expected string for sorting instead of true/false */
          if (requiresTimeEntry === 'not_expected') {
            return requiresTimeEntry;
          }
          return String(hasTimeEntries);
        },
        Cell: (allData) => {
          const { user, userDailyTimeEntries, dailyTimeEntries } = allData.original;
          const userCurrentDailyTimeEntry = userDailyTimeEntries[index];
          return returnFormattedCell(user, userCurrentDailyTimeEntry, dailyTimeEntries, index);
        },
        minWidth: 75,
      });
    });

    const allColumns = [
      ...userColumns,
      ...dayColumns,
    ];
    return allColumns;
  };


  const { userTimesheets, interval } = props;
  return (
    <div>
      <Panel header={`${interval.length} Day Status`}>
        {userTimesheets.length > 0
          && (
          <ReactTable
            data={userTimesheets}
            filterable
            defaultFilterMethod={(filter, row) => {
              return defaultTableFiltering(filter, row);
            }}
            SubComponent={(row) => {
              return (
                <div>
                  <NotificationDetails
                    selectedUser={row.original.user}
                  />
                </div>
              );
            }}
            columns={renderColumns()}
            defaultPageSize={10}
            className="-striped -highlight"
          />
          ) }
      </Panel>
    </div>
  );
};

TimesheetCollectionStatusLayout.propTypes = proptypes;

const mapDispatchToProps = {
  setDailyTimeEntryStatusAction: setDailyTimeEntryStatus,
};

const withRedux = connect(null, mapDispatchToProps);

const enhance = compose(
  queryAllWeeklyTimesheetsGQL,
  withRedux,
);

export default enhance(TimesheetCollectionStatusLayout);
